// src/pages/costing/PageRM.jsx
import { useState, useMemo } from "react";
import { fmt2, fmt4 } from "../../utils/costingUtils";
import { ModalWrap } from "../../components/ModalWrap";

export function PageRM({ rms, search, setSearch, sfgs, sfgFormulas, fgs, fgFormulas, priceHist, onUpdate, onAdd, onHistory }) {
  const [editing, setEditing] = useState({});
  const [hovCol,  setHovCol]  = useState(null);

  const sfgUseMap = useMemo(() => {
    const m = {};
    for (const rm of rms)
      m[rm.id] = sfgs.filter(s => (sfgFormulas[s.id]||[]).some(f => (f.rmId??f.id) === rm.id));
    return m;
  }, [rms, sfgs, sfgFormulas]);

  const fgUseMap = useMemo(() => {
    const m = {};
    for (const rm of rms)
      m[rm.id] = fgs.filter(fg => (fgFormulas[fg.id]||[]).some(f => (f.componentId??f.id) === rm.id && (f.type==="RM"||f.type==="rm")));
    return m;
  }, [rms, fgs, fgFormulas]);
  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Raw Materials</div>
          <div className="page-subtitle">Master price list · {rms.length} items</div>
        </div>
        <div className="page-header-right">
          <button className="btn-primary" onClick={onAdd}>+ Add RM</button>
        </div>
      </div>
      <div className="action-bar">
        <input className="form-input" style={{ width:240 }} placeholder="Search by name or code…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="card">
        <div className="table-wrap">
        <table className="data-table" data-hov-col={hovCol ?? undefined} onMouseLeave={() => setHovCol(null)}>
          <thead><tr>
            <th>RM Code</th>
            <th>RM Name</th>
            <th>Unit</th>
            <th style={{ textAlign:"right" }}>Price (AED)</th>
            <th>Used in SFGs</th>
            <th>Used in FGs</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rms.map(rm => {
              const sfgUse = sfgUseMap[rm.id] || [];
              const fgUse  = fgUseMap[rm.id]  || [];
              const isEdit = editing[rm.id] !== undefined;
              const hist   = priceHist.filter(h => (h.rmId??h.itemId) === rm.id);
              return (
                <tr key={rm.id}>
                  <td onMouseEnter={() => setHovCol(0)}><span className="badge badge-gray">{rm.id}</span></td>
                  <td className="fw-600" onMouseEnter={() => setHovCol(1)}>{rm.name}</td>
                  <td onMouseEnter={() => setHovCol(2)}>{rm.unit}</td>
                  <td style={{ textAlign:"right" }} onMouseEnter={() => setHovCol(3)}>
                    {isEdit ? (
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:6 }}>
                        <input className="form-input input-right" style={{ width:110 }} type="number" step="0.0001"
                          value={editing[rm.id]} onChange={e => setEditing(x => ({...x, [rm.id]:e.target.value}))}
                          onKeyDown={e => {
                            if (e.key==="Enter") { onUpdate(rm.id, parseFloat(editing[rm.id])); setEditing(x => { const n={...x}; delete n[rm.id]; return n; }); }
                            if (e.key==="Escape") setEditing(x => { const n={...x}; delete n[rm.id]; return n; });
                          }} autoFocus />
                        <button className="btn-primary" onClick={() => { onUpdate(rm.id, parseFloat(editing[rm.id])); setEditing(x => { const n={...x}; delete n[rm.id]; return n; }); }}>Save</button>
                        <button className="btn-small" onClick={() => setEditing(x => { const n={...x}; delete n[rm.id]; return n; })}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:8 }}>
                        <span className="fw-600">{(rm.price||0).toFixed(4)}</span>
                        <button className="btn-small" onClick={() => setEditing(x => ({...x, [rm.id]:String(rm.price)}))}>Edit</button>
                      </div>
                    )}
                  </td>
                  <td onMouseEnter={() => setHovCol(4)}>
                    {sfgUse.length ? sfgUse.map(s => <div key={s.id} style={{ fontSize:11, color:"#6b21a8" }}>{s.name}</div>) : <span className="text-muted">—</span>}
                  </td>
                  <td onMouseEnter={() => setHovCol(5)}>
                    {fgUse.length ? fgUse.map(f => <div key={f.id} style={{ fontSize:11, color:"#1e40af" }}>{f.name}</div>) : <span className="text-muted">—</span>}
                  </td>
                  <td onMouseEnter={() => setHovCol(6)}>
                    <button className="btn-small" onClick={() => onHistory(rm)}>History ({hist.length})</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

export function AddRMModal({ onSave, onCancel }) {
  const [f, setF] = useState({ id:"", name:"", unit:"kg", price:"" });
  const valid = f.id.trim() && f.name.trim();
  return (
    <ModalWrap title="Add Raw Material" onClose={onCancel}>
      {[
        ["RM Code",    "id",    "text"],
        ["RM Name",    "name",  "text"],
        ["Unit",       "unit",  "text"],
        ["Price (AED)","price", "number"],
      ].map(([l,k,t]) => (
        <div key={k} className="form-row">
          <span className="form-label">{l}</span>
          <input className={`form-input${t==="number"?" input-right":""}`} style={{ flex:1 }} type={t}
            value={f[k]} onChange={e => setF(x => ({...x, [k]:e.target.value}))} />
        </div>
      ))}
      <div className="form-sep" />
      <div className="form-footer">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" disabled={!valid} onClick={() => { if (valid) onSave(f); }}>Add</button>
      </div>
    </ModalWrap>
  );
}

export function RMHistModal({ rm, priceHist, onUpdate, onClose }) {
  const [newPrice, setNewPrice] = useState(String(rm.price));
  const [note,     setNote]     = useState("");
  const hist = [...priceHist.filter(h => (h.rmId??h.itemId) === rm.id)]
    .sort((a,b) => (b.changedAt||b.timestamp||"").localeCompare(a.changedAt||a.timestamp||""));
  return (
    <ModalWrap title={rm.name} onClose={onClose}>
      <div style={{ fontSize:11, color:"#bbb", marginTop:-10, marginBottom:14 }}>
        {rm.id} · AED {(rm.price||0).toFixed(4)}/{rm.unit}
      </div>
      {hist.map((h,i) => (
        <div key={i} style={{ display:"flex", gap:10, padding:"7px 0", borderBottom:"1px solid #f5f5f5", fontSize:12 }}>
          <div style={{ fontSize:10, color:"#bbb", minWidth:100 }}>{(h.changedAt||h.timestamp||"").slice(0,10)}</div>
          <div style={{ flex:1, color:"#888" }}>{h.changedBy||h.updatedBy||""}{h.note?` · ${h.note}`:""}</div>
          <div className="fw-700">AED {fmt2(h.newPrice??h.price)}</div>
        </div>
      ))}
      {!hist.length && <div style={{ color:"#bbb", fontSize:12, padding:"8px 0 14px" }}>No history yet.</div>}
      <div className="form-sep" />
      <div style={{ fontSize:11, fontWeight:700, marginBottom:10 }}>Log new price</div>
      <div className="form-row">
        <span className="form-label">New price (AED)</span>
        <input className="form-input input-right" style={{ width:130 }} type="number" step="0.0001"
          value={newPrice} onChange={e => setNewPrice(e.target.value)} />
      </div>
      <div className="form-row">
        <span className="form-label">Note</span>
        <input className="form-input" style={{ flex:1 }} value={note}
          onChange={e => setNote(e.target.value)} placeholder="Reason for change…" />
      </div>
      <button className="btn-primary" style={{ width:"100%", marginTop:4 }}
        onClick={() => { const p = parseFloat(newPrice); if (!isNaN(p) && p > 0) { onUpdate(rm.id, p, note); onClose(); } }}>
        Save new price
      </button>
    </ModalWrap>
  );
}
